import type { PointerEventState } from '@blocksuite/block-std';
import type {
  GfxElementModelView,
  GfxLocalElementModel,
  GfxPrimitiveElementModel,
  SupportedEvent,
} from '@blocksuite/block-std/gfx';

import { Bound } from '@blocksuite/global/utils';

import { DefaultModeDragType, DefaultToolExt } from './ext.js';

export class CanvasElementEventExt extends DefaultToolExt {
  private _currentStackedElm: (
    | GfxPrimitiveElementModel
    | GfxLocalElementModel
  )[] = [];

  override supportedDragTypes: DefaultModeDragType[] = [
    DefaultModeDragType.None,
  ];

  private _callInReverseOrder(
    callback: (
      elm: GfxPrimitiveElementModel | GfxLocalElementModel,
      view: GfxElementModelView
    ) => void,
    arr = this._currentStackedElm
  ) {
    for (let i = arr.length - 1; i >= 0; i--) {
      const elm = arr[i];
      const view = this.gfx.view.get(elm) as GfxElementModelView;

      callback(elm, view);
    }
  }

  private _dispatchEventInReverseOrder(
    eventName: SupportedEvent,
    evt: PointerEventState
  ): void {
    this._callInReverseOrder(elm => {
      const view = this.gfx.view.get(elm) as GfxElementModelView;

      view.dispatch(eventName, evt);
    });
  }

  override click(_evt: PointerEventState): void {
    this._dispatchEventInReverseOrder('click', _evt);
  }

  override dblClick(_evt: PointerEventState): void {
    this._dispatchEventInReverseOrder('dblclick', _evt);
  }

  override pointerDown(_evt: PointerEventState): void {
    this._dispatchEventInReverseOrder('pointerdown', _evt);
  }

  override pointerMove(_evt: PointerEventState): void {
    const [x, y] = this.gfx.viewport.toModelCoord(_evt.x, _evt.y);
    const hoveredElm = this.gfx.grid.search(new Bound(x, y, 1, 1), {
      filter: ['canvas', 'local'],
    }) as (GfxPrimitiveElementModel | GfxLocalElementModel)[];
    const stackedElmMap = new Map(
      this._currentStackedElm.map((val, index) => [val, index])
    );

    this._callInReverseOrder((elm, view) => {
      if (stackedElmMap.has(elm)) {
        const idx = stackedElmMap.get(elm)!;
        this._currentStackedElm.splice(idx, 1);
        view.dispatch('pointermove', _evt);
      } else {
        view.dispatch('pointerenter', _evt);
      }
    }, hoveredElm);
    this._callInReverseOrder((_, view) => view.dispatch('pointerleave', _evt));
    this._currentStackedElm = hoveredElm;
  }

  override pointerUp(_evt: PointerEventState): void {
    this._dispatchEventInReverseOrder('pointerup', _evt);
  }
}
